import React, { Component } from "react";
import { connect } from "react-redux";
import * as learningPathActions from "../../redux/actions/userAssignmentsListActions";
import { bindActionCreators } from "redux";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import LearningPathNoAssignmentsView from "../../views/LearningPath/LearningPathNoAssignmentsView";
import clsx from "clsx";
import SearchBar from "../../components/SearchBar/SearchBar";
import {
  learnLearningPathListView,
  learnLearningPathPathView
} from "../../routes/LearningCenterRoutes";
import FullPageLayout from "../../components/FullPageLayout/index";
import { Typography, Box } from "@mui/material";
import { NavLink, Route } from "react-router-dom";
import withStyles from "@mui/styles/withStyles";
import { compose } from "redux";
import { push } from "connected-react-router";
import SearchPageLayout from "../../components/FullPageLayout/SearchPageLayout";
import SearchFilters from "../../components/SearchFilters/SearchFilters";
import { learningPathSearchFilters } from "../../constants";
import { learningPathListSortByMethods } from "../../constants/index";
import { withTranslation } from "react-i18next";
import LearningPathListView from "../../views/LearningPath/LearningPathListView";
import { makeGetEntities } from "../../redux/selectors/selectors";
import { ExpandedAssignmentsProvider } from "../../context/ExpandedAssignmentsContext";

const selector = makeGetEntities();

const assignmentsPerPageOptions = ["5", "10", "15", "All"];

const styles = theme => ({
  link: {
    textDecoration: "none",
    color: theme.palette.text.secondary,
    width: 200,
    borderRadius: 0,
    border: `solid 1px ${theme.palette.secondary[300]}`,
    height: 48,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "&.active": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      "&.left": {
        marginRight: "-1px",
        zIndex: 1
      },
      "&.right": {
        marginLeft: "-1px",
        zIndex: 1
      }
    }
  }
});

const NoSearchResultsView = ({ t, searchValue }) => (
  <Box mt={3}>
    <Typography color="primary" variant="h6">
      {`${t("learningCenterLearningPath.noSearchResultsMesssagePartOne")}`}
      <b>{searchValue}</b>
      {`${t("learningCenterLearningPath.noSearchResultsMesssagePartTwo")}`}
    </Typography>
  </Box>
);

class LearningCenterLearningPath extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchValue: "",
      assignments: null,
      sortByOption: learningPathListSortByMethods[0],
      perPage: assignmentsPerPageOptions[0],
      page: 0,
      filters: {
        [learningPathSearchFilters.learningPathAssignments]: {
          checked: true,
          disabled: true
        }
      }
    };
  }

  fetchAssignments = () => {
    let params = {};
    params.type = "admin_enrolled";
    if (this.state.sortByOption) {
      params.order = this.state.sortByOption.value;
    }
    if (this.state.perPage) {
      params.per_page = this.state.perPage;
    }
    if (this.state.page >= 0) {
      params.page = this.state.page + 1;
    }
    if (this.state.searchValue) {
      params.search = this.state.searchValue;
      params.filters = "learning_path_assignments";
    }
    this.props.actions.fetchUserAssignmentsList(params);
  };

  /**
   * When the user submits a search value, sets the current path to the list view if the user is
   * on the path view and fetches new assignments with the search value/filters
   */
  handleSearchSubmit = searchValue => {
    const {
      match: { path }
    } = this.props;
    this.setState({ searchValue }, () => {
      if (path !== learnLearningPathListView) {
        this.props.push(learnLearningPathListView);
      }
      this.fetchAssignments();
    });
  };

  handleSortByOptionChange = newSortByOption => {
    this.setState({ sortByOption: newSortByOption, page: 0 }, () =>
      this.fetchAssignments()
    );
  };

  componentDidUpdate() {
    const { assignments } = this.props;
    if (this.state.assignments !== assignments) {
      this.setState({ assignments });
    }
  }

  componentDidMount() {
    this.fetchAssignments();
  }

  handleFiltersChange = filters => {
    this.setState({ filters }, () => {
      this.fetchAssignments();
    });
  };

  /**
   * Reset the filters back to their default values (all unchecked except Learning Path Assignments which is
   * checked by default)
   */
  handleClearAll = () => {
    const filters = this.state.filters;
    Object.keys(this.state.filters).forEach(filter => {
      filters[filter] = {
        ...filters[filter],
        checked: filter === learningPathSearchFilters.learningPathAssignments
      };
    });
    this.setState({ searchValue: "", filters }, this.fetchAssignments);
  };

  /**
   * Called when user changes the amount of results shown per page
   * @param {string} newAssignmentsPerPageValue The new per page value
   */
  handleAssignmentsPerPageChange = newAssignmentsPerPageValue => {
    this.setState({ perPage: newAssignmentsPerPageValue, page: 0 }, () =>
      this.fetchAssignments()
    );
  };

  /**
   * Called when user hits next or previous button
   * @param {number} newAssignmentsPageValue The new page value
   */
  handleAssignmentsPageChange = newAssignmentsPageValue => {
    this.setState({ page: newAssignmentsPageValue }, () => {
      this.fetchAssignments();
    });
  };
  /**
   * Shows the loading spinner, the no assignments view, or the learning path list/path view
   * depending on the current state
   */
  getLearningPathAssignmentsView = () => {
    const { assignments, searchValue } = this.state;
    const { numTotalAssignments, loading, t } = this.props;
    if (loading) {
      return <LoadingSpinner visible={loading} />;
    } else if (!assignments || assignments.length === 0) {
      return (
        <Box mt={2}>
          {searchValue ? (
            <NoSearchResultsView searchValue={searchValue} t={t} />
          ) : (
            <LearningPathNoAssignmentsView />
          )}
        </Box>
      );
    } else {
      return (
        // props passed to custom routes get passed to LearningPathListView or LearningPathPathView
        <Route
          render={() => (
            <ExpandedAssignmentsProvider>
              <LearningPathListView
                assignments={assignments}
                numTotalAssignments={numTotalAssignments}
                sortOptions={learningPathListSortByMethods}
                sortByOption={this.state.sortByOption}
                onSortByOptionChange={this.handleSortByOptionChange}
                onAssignmentsPerPageChange={this.handleAssignmentsPerPageChange}
                onPageChange={this.handleAssignmentsPageChange}
                page={this.state.page}
                perPage={this.state.perPage}
                assignmentsPerPageOptions={assignmentsPerPageOptions}
                error={this.props.error}
                refetchAssignments={() => this.fetchAssignments()}
              />
            </ExpandedAssignmentsProvider>
          )}
          exact
          path={learnLearningPathListView}
        />
      );
    }
  };

  render() {
    const { theme, classes, t } = this.props;
    const { searchValue, filters } = this.state;

    const searchBar = (
      <SearchBar
        background={theme.palette.inputBackground}
        placeholder={t("learningCenterLearningPath.searchPlaceholder")}
        onSearchSubmit={this.handleSearchSubmit}
      />
    );

    const learningPathAssignmentsView = this.getLearningPathAssignmentsView();

    if (searchValue) {
      return (
        <SearchPageLayout
          title={t("learningCenterLearningPath.searchPlaceholder")}
          searchValue={searchValue}
          onSearchSubmit={this.handleSearchSubmit}
        >
          <SearchFilters
            onFiltersChange={this.handleFiltersChange}
            filters={filters}
            onClearAllClick={() => this.handleClearAll()}
          />
          {learningPathAssignmentsView}
        </SearchPageLayout>
      );
    } else {
      return (
        <FullPageLayout
          headerActions={searchBar}
          title={t("learningCenterLearningPath.title")}
        >
          <Box display="flex" mt={4} justifyContent="center ">
            <NavLink
              className={clsx(classes.link, "left")}
              style={{ display: "none" }}
              to={learnLearningPathListView}
            >
              <Typography variant="button">
                {t("learningCenterLearningPath.listViewButton")}
              </Typography>
            </NavLink>
            <NavLink
              className={clsx(classes.link, "right")}
              to={learnLearningPathPathView}
              style={{ display: "none" }}
            >
              <Typography variant="button">
                {t("learningCenterLearningPath.pathViewButton")}
              </Typography>
            </NavLink>
          </Box>
          {learningPathAssignmentsView}
        </FullPageLayout>
      );
    }
  }
}
const TranslatedLearningCenterLearningPath = withTranslation()(
  LearningCenterLearningPath
);

const mapStateToProps = state => ({
  assignments: selector(state.userAssignmentsListState, {
    type: "userAssignment",
    id: state.userAssignmentsListState.displayedCourses.admin_enrolled
  }),
  numTotalAssignments: state.userAssignmentsListState.totalCount,
  loading: state.userAssignmentsListState.loading,
  error: state.userAssignmentsListState.error
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(learningPathActions, dispatch),
  push
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStyles(styles, { withTheme: true })
)(TranslatedLearningCenterLearningPath);
